"use client";

import { type AnimationEvent, useMemo } from "react";
import { Box, Center, Icon, Text } from "@chakra-ui/react";
import { LuLightbulb } from "react-icons/lu";
import { useTranslations } from "next-intl";
import { NextLink } from "@/_components/ui/NextLink";
import { SELF_ADS_COLORS } from "./colors";
import { DEFAULT_SELF_ADS_PILL_MESSAGES } from "./messages";
import {
  PILL_ENTER_MS,
  PILL_EXIT_MS,
  PILL_ROTATE_INTERVAL_MS,
  useRotatingMessage,
} from "./useRotatingMessage";

export { DEFAULT_SELF_ADS_PILL_MESSAGES } from "./messages";
export {
  PILL_ENTER_MS,
  PILL_EXIT_MS,
  PILL_ROTATE_INTERVAL_MS,
} from "./useRotatingMessage";

export interface SelfAdsPillProps {
  messages?: readonly string[];
  ctaLabel?: string;
  href: string;
  /** How often to rotate to a new random message. Default: 20s. */
  intervalMs?: number;
}

export function SelfAdsPill({
  messages,
  ctaLabel,
  href,
  intervalMs = PILL_ROTATE_INTERVAL_MS,
}: SelfAdsPillProps) {
  const t = useTranslations("self_ads.pill");

  const resolvedMessages = useMemo(() => {
    if (messages?.length) return messages;
    const fromI18n = t.raw("messages");
    if (Array.isArray(fromI18n) && fromI18n.length > 0) {
      return fromI18n as string[];
    }
    return DEFAULT_SELF_ADS_PILL_MESSAGES;
  }, [messages, t]);

  const resolvedCta = ctaLabel ?? t("cta");

  const { message, animState, onExitAnimationEnd } = useRotatingMessage({
    messages: resolvedMessages,
    intervalMs,
  });

  if (!message) return null;

  const handleAnimationEnd = (event: AnimationEvent<HTMLParagraphElement>) => {
    if (event.target !== event.currentTarget) return;
    onExitAnimationEnd(event.animationName);
  };

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      w="fit-content"
      maxW="full"
      gap={3}
      px={{ base: 3, md: 4 }}
      py={2}
      rounded="full"
      bg={SELF_ADS_COLORS.shellBg}
      borderWidth="1px"
      borderStyle="solid"
      borderColor={SELF_ADS_COLORS.cardBorder}
    >
      <Center
        boxSize={7}
        rounded="full"
        bg={SELF_ADS_COLORS.accentMuted}
        flexShrink={0}
      >
        <Icon as={LuLightbulb} boxSize={6} color={SELF_ADS_COLORS.label} />
      </Center>

      <Text
        fontSize={{ base: "xs", md: "sm" }}
        color={SELF_ADS_COLORS.body}
        lineHeight="short"
        overflow="hidden"
        data-state={animState}
        onAnimationEnd={handleAnimationEnd}
        _open={{
          animationName: "slide-from-bottom, fade-in",
          animationDuration: `${PILL_ENTER_MS}ms`,
          animationTimingFunction: "ease-out",
        }}
        _closed={{
          animationName: "slide-to-top, fade-out",
          animationDuration: `${PILL_EXIT_MS}ms`,
          animationTimingFunction: "ease-in",
        }}
        _motionReduce={{
          animationName: "fade-in, fade-out",
          animationDuration: "1ms !important",
        }}
      >
        {message}
        <Text as="span">{" — "}</Text>
        <NextLink
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          display="inline"
          color={SELF_ADS_COLORS.label}
          fontWeight="semibold"
          fontSize="inherit"
          lineHeight="inherit"
          _hover={{
            color: SELF_ADS_COLORS.accent,
            textDecoration: "underline",
          }}
        >
          {resolvedCta}
        </NextLink>
      </Text>
    </Box>
  );
}
